/**
 * Heavy markdown/document export helpers.
 *
 * This module is intentionally separate from utils/index.ts so that the
 * large dependencies it brings in (marked ~100KB, docx ~500KB, file-saver)
 * are NOT included in the initial JS bundle. It is only imported by modules
 * that live inside lazy-loaded route chunks (session-page, jan, recording).
 */
import { marked } from "marked";
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  ImageRun,
  HeadingLevel,
  AlignmentType,
  LevelFormat,
  Table,
  TableRow,
  TableCell,
  WidthType,
  type IRunOptions,
} from "docx";
import { saveAs } from "file-saver";
import backendAxios from "../api/backend/axios";
import {
  extractJanS3Key,
  foldBlankHardBreaks,
  ZERO_WIDTH_SPACE,
} from "./index";
import { decodeHtmlCharacterReferences } from "./htmlCharacterReferences";

// ── Internal constants ────────────────────────────────────────────────────────

const MARKDOWN_CONFIG = {
  MARKED_OPTIONS: {
    breaks: false,
    gfm: true,
  },
};

interface WordImageData {
  data: ArrayBuffer;
  width: number;
  height: number;
}

const WORD_IMAGE_MAX_WIDTH = 500;

// ── Markdown → HTML ───────────────────────────────────────────────────────────

export function markdownToHtml(markdown: string): string {
  if (!markdown?.trim()) return "";

  try {
    // Configure marked for clean HTML output
    marked.setOptions(MARKDOWN_CONFIG.MARKED_OPTIONS);

    // Clean up the markdown before processing and convert pseudo-markdown to proper markdown
    let cleanMarkdown = decodeHtmlCharacterReferences(
      foldBlankHardBreaks(markdown)
    )
      .replace(/\n\s*\n\s*\n+/g, "\n\n") // Replace multiple empty lines with just one
      .replace(/^\s+|\s+$/g, "") // Trim whitespace from start and end
      .replace(/\n\s*$/gm, "\n") // Remove trailing spaces from lines
      .replace(/^[\s\n]*Here is.*?:\s*/i, "") // Remove AI preamble text from markdown too
      .replace(/^[\s\n]*I've.*?:\s*/i, "") // Remove other preamble patterns
      // Remove excessive escaping that might be causing issues
      .replace(/\\(.)/g, "$1") // Remove backslashes before any character
      // Remove unwanted HTML tags that Claude might generate
      .replace(/<\/?u>/g, "") // Remove <u> and </u> tags (underline)

      // Convert headers ending with colons to proper markdown headers (improved pattern)
      // First handle any text that looks like headers but might not be properly formatted
      .replace(/^([A-Z][A-Za-z\s()]+):\s*$/gm, "## $1") // Match lines ending with colon, allow parentheses and spaces
      .replace(/^([A-Z][A-Za-z\s()]+):\s*\n/gm, "## $1\n") // Handle lines that might have content after the colon
      // Handle edge cases for headers without proper capitalization patterns
      .replace(/^([^#\n]{2,50}):\s*$/gm, (match, header) => {
        // Only convert to header if it looks like a real header (not just any text with colon)
        if (
          header.match(
            /^(Current|Mental|Session|Next|Treatment|Assessment|Plan|Summary|Problem|Presenting|History|Background|Diagnosis|Intervention|Progress|Goal|Objective|Recommendation|Follow|Note|Document|Analysis|Evaluation|Review|Report|Clinical|Patient|Client)/i
          )
        ) {
          return `## ${header}`;
        }
        return match;
      })
      // Ensure bullet points are properly formatted (handle indented bullets)
      .replace(/^\s*-\s+/gm, "- ") // Normalize bullet points
      // Handle cases where bullet points might be the only content under headers
      .replace(/^(## [^\n]+)\n\n(-[^\n]+(?:\n-[^\n]+)*)/gm, "$1\n\n$2")
      // Reduce excessive spacing between sections
      .replace(/\n\n\n+/g, "\n\n"); // Ensure max 2 consecutive line breaks

    // marked.parse returns a string in synchronous mode
    let result = marked.parse(cleanMarkdown);

    if (typeof result === "string") {
      // Post-process the HTML to clean up spacing issues and improve cross-platform compatibility
      result = result
        .replace(/<p>\s*<\/p>/g, "") // Remove empty paragraphs
        .replace(/<p><br\s*\/?><\/p>/g, "") // Remove paragraphs with just line breaks
        .replace(/<p>\s*&nbsp;\s*<\/p>/g, "") // Remove paragraphs with just non-breaking spaces
        .replace(/\n\s*\n+/g, "\n") // Remove extra line breaks
        .replace(/<\/p>\s*<p>/g, "</p><p>") // Minimize paragraph spacing
        .replace(/<\/h([1-6])>\s*<p>/g, "</h$1><p>") // Minimize spacing after headers
        .replace(/<\/ul>\s*<p>/g, "</ul><p>") // Minimize spacing after lists
        .replace(/<\/ol>\s*<p>/g, "</ol><p>") // Minimize spacing after ordered lists
        .replace(/<\/li>\s*<li>/g, "</li><li>") // Minimize spacing between list items
        .replace(/<ul>\s*<li>/g, "<ul><li>") // Minimize spacing at start of lists
        .replace(/<ol>\s*<li>/g, "<ol><li>") // Minimize spacing at start of ordered lists
        .replace(/<\/li>\s*<\/ul>/g, "</li></ul>") // Minimize spacing at end of lists
        .replace(/<\/li>\s*<\/ol>/g, "</li></ol>") // Minimize spacing at end of ordered lists
        // Remove empty or whitespace-only paragraphs that might have been missed
        .replace(/<p[^>]*>\s*<\/p>/g, "") // Remove any empty paragraphs including ones with attributes
        // Ensure headers have explicit bold formatting in HTML
        .replace(
          /<h1([^>]*)>(.*?)<\/h1>/gi,
          '<h1$1><strong style="font-weight: bold !important;">$2</strong></h1>'
        )
        .replace(
          /<h2([^>]*)>(.*?)<\/h2>/gi,
          '<h2$1><strong style="font-weight: bold !important;">$2</strong></h2>'
        )
        .replace(
          /<h3([^>]*)>(.*?)<\/h3>/gi,
          '<h3$1><strong style="font-weight: bold !important;">$2</strong></h3>'
        )
        .replace(
          /<h4([^>]*)>(.*?)<\/h4>/gi,
          '<h4$1><strong style="font-weight: bold !important;">$2</strong></h4>'
        )
        .replace(
          /<h5([^>]*)>(.*?)<\/h5>/gi,
          '<h5$1><strong style="font-weight: bold !important;">$2</strong></h5>'
        )
        .replace(
          /<h6([^>]*)>(.*?)<\/h6>/gi,
          '<h6$1><strong style="font-weight: bold !important;">$2</strong></h6>'
        )
        // Remove conflicting inline styles but preserve our new bold styles
        .replace(/style="(?![^"]*font-weight[^"]*)[^"]*"/g, "")
        .replace(/\s+/g, " ") // Replace multiple spaces with single space
        .replace(/>\s+</g, "><") // Remove spaces between tags
        // Remove any leading/trailing empty elements - more specific to avoid removing content tags
        .replace(/^(\s*<(p|div|span)[^>]*>\s*<\/(p|div|span)>\s*)*/, "") // Remove only empty p, div, span tags
        .replace(/(\s*<(p|div|span)[^>]*>\s*<\/(p|div|span)>\s*)*$/, "") // Remove only empty p, div, span tags at end
        .trim();

      return addExplicitBlockSpacing(result);
    }

    return "";
  } catch (error) {
    console.error("Error converting markdown to HTML:", error);
    // Fallback to simple conversion if marked fails
    return markdown
      .replace(/\n\n+/g, "</p><p>")
      .replace(/^/, "<p>")
      .replace(/$/, "</p>");
  }
}

function isEmptyParagraph(element: Element): boolean {
  return (
    element.tagName === "P" &&
    !element.querySelector("img") &&
    !element.textContent?.replaceAll(ZERO_WIDTH_SPACE, "").trim()
  );
}

/**
 * Cliniko (Tiptap) and Practice Better (Froala) paste blocks with no gap, so
 * the gap has to be an element. Halaxy (CKEditor 5) drops our `margin:0`, adds
 * its own paragraph margins, and keeps an empty `<p>` but drops an empty
 * `<div>`. So the gap between blocks is an empty `<div>`, which Halaxy drops,
 * and a blank line the clinician added stays an empty `<p>`, which Halaxy keeps.
 * Zeroing block margins stops targets that keep styles (Word, Google Docs,
 * Gmail) from stacking their own margin on top of the gap. Spacers hold a
 * non-breaking space because Google Docs drops pasted blocks that contain
 * only a `<br>`.
 */
function addExplicitBlockSpacing(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const spacers = new Set<Element>();
  const createSpacer = (tagName: "div" | "p") => {
    const spacer = doc.createElement(tagName);
    if (tagName === "p") spacer.setAttribute("style", "margin:0");
    spacer.textContent = "\u00a0";
    spacers.add(spacer);
    return spacer;
  };

  for (const block of Array.from(doc.body.children)) {
    if (isEmptyParagraph(block)) {
      block.replaceWith(createSpacer("p"));
      continue;
    }
    const retainedStyle = block.getAttribute("style");
    block.setAttribute(
      "style",
      retainedStyle ? `margin:0;${retainedStyle}` : "margin:0"
    );
    block.after(createSpacer("div"));
  }

  const isSpacer = (element: Element | null) =>
    element !== null && spacers.has(element);
  while (isSpacer(doc.body.firstElementChild)) {
    doc.body.firstElementChild?.remove();
  }
  while (isSpacer(doc.body.lastElementChild)) {
    doc.body.lastElementChild?.remove();
  }

  return doc.body.innerHTML;
}

// ── Markdown → Word (.docx) ───────────────────────────────────────────────────

/**
 * Fetch all markdown images via backend proxy and return their data for Word embedding.
 * Extracts S3 keys from jan-image: placeholders or presigned S3 URLs, then fetches
 * through /api/s3-image to avoid CORS issues.
 */
async function fetchImagesForWord(
  markdown: string
): Promise<{ imageMap: Map<string, WordImageData> }> {
  const imageMap = new Map<string, WordImageData>();
  const imageRegex = /!\[[^\]]*\]\(([^)]+)\)/g;
  const matches = [...markdown.matchAll(imageRegex)];

  // Inline data URIs (used by public-Jan mermaid export — no S3, no fetch).
  // Decode the bytes directly and measure dimensions via an Image element,
  // matching the clamp the S3 branch applies below.
  for (const match of matches) {
    const url = match[1];
    if (imageMap.has(url) || !url.startsWith("data:image/png;base64,")) {
      continue;
    }
    try {
      const b64 = url.slice("data:image/png;base64,".length);
      const binary = atob(b64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      const data = bytes.buffer;
      const blobUrl = URL.createObjectURL(
        new Blob([data], { type: "image/png" })
      );
      try {
        const img = new Image();
        await new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = () => reject(new Error("data URI image load failed"));
          img.src = blobUrl;
        });
        let width = img.naturalWidth || WORD_IMAGE_MAX_WIDTH;
        let height = img.naturalHeight || 300;
        if (width > WORD_IMAGE_MAX_WIDTH) {
          const scale = WORD_IMAGE_MAX_WIDTH / width;
          width = WORD_IMAGE_MAX_WIDTH;
          height = Math.round(height * scale);
        }
        imageMap.set(url, { data, width, height });
      } finally {
        URL.revokeObjectURL(blobUrl);
      }
    } catch (err) {
      console.warn("[markdownToWordDocument] data URI decode failed:", err);
    }
  }

  // Extract unique S3 keys, mapping original URL → S3 key
  const urlToKey = new Map<string, string>();
  for (const match of matches) {
    const url = match[1];
    if (urlToKey.has(url)) continue;
    const key = extractJanS3Key(url);
    if (key) urlToKey.set(url, key);
  }

  const processedKeys = new Set<string>();

  await Promise.all(
    [...urlToKey.entries()].map(async ([originalUrl, s3Key]) => {
      if (processedKeys.has(s3Key)) {
        // Same S3 key already fetched — find the URL we fetched it under and
        // copy its cached result to this URL.
        const cachedUrl = [...urlToKey.entries()].find(
          ([, k]) => k === s3Key
        )?.[0];
        if (cachedUrl !== undefined) {
          const existing = imageMap.get(cachedUrl);
          if (existing) imageMap.set(originalUrl, existing);
        }
        return;
      }
      processedKeys.add(s3Key);

      try {
        const isSvg = s3Key.endsWith(".svg");
        // SVGs need server-side Puppeteer conversion to PNG (foreignObject won't render in Word)
        const endpoint = isSvg ? "/api/s3-image/convert" : "/api/s3-image";
        const response = await backendAxios.get(endpoint, {
          params: { key: s3Key },
          responseType: "arraybuffer",
        });

        const data = response.data as ArrayBuffer;

        // Get dimensions via Image element
        const blobUrl = URL.createObjectURL(
          new Blob([data], { type: "image/png" })
        );
        try {
          const img = new Image();
          await new Promise<void>((resolve, reject) => {
            img.onload = () => resolve();
            img.onerror = () => reject(new Error("Failed to load image"));
            img.src = blobUrl;
          });

          let width = img.naturalWidth || WORD_IMAGE_MAX_WIDTH;
          let height = img.naturalHeight || 300;
          if (width > WORD_IMAGE_MAX_WIDTH) {
            const scale = WORD_IMAGE_MAX_WIDTH / width;
            width = WORD_IMAGE_MAX_WIDTH;
            height = Math.round(height * scale);
          }

          imageMap.set(originalUrl, { data, width, height });
        } finally {
          URL.revokeObjectURL(blobUrl);
        }
      } catch (error) {
        console.warn(
          `[markdownToWordDocument] Failed to fetch image: ${s3Key}`,
          error
        );
      }
    })
  );

  return { imageMap };
}

/** Convert Markdown to Word Document (.docx) and trigger download. */
export async function markdownToWordDocument(
  markdown: string,
  filename: string
): Promise<void> {
  if (!markdown) return;

  try {
    // Fetch image data via backend proxy (avoids CORS issues with S3)
    const { imageMap } = await fetchImagesForWord(markdown);

    // Clean HTML tags from markdown before processing
    const cleanMarkdown = decodeHtmlCharacterReferences(markdown)
      .replace(/<\/?u>/g, "") // Remove <u> and </u> tags (underline)
      .replace(/<\/?i>/g, "") // Remove <i> and </i> tags
      .replace(/<\/?b>/g, "") // Remove <b> and </b> tags
      .replace(/<\/?em>/g, "") // Remove <em> and </em> tags
      .replace(/<\/?strong>/g, "") // Remove <strong> and </strong> tags
      .replace(/<\/?span[^>]*>/g, "") // Remove span tags with any attributes
      .replace(/<\/?div[^>]*>/g, "") // Remove div tags with any attributes
      .replace(/<br\s*\/?>/g, "\n") // Convert <br> tags to line breaks
      .replace(/<[^>]+>/g, "") // Remove any other HTML tags
      .trim();

    // Parse markdown and convert to Word document structure
    const elements = parseMarkdownForWord(cleanMarkdown, imageMap);

    // Create a new Word document
    const doc = new Document({
      styles: {
        paragraphStyles: [{ id: WORD_BODY_STYLE, name: WORD_BODY_STYLE }],
      },
      numbering: {
        config: [
          {
            reference: "default-numbering",
            levels: [
              {
                level: 0,
                format: LevelFormat.DECIMAL,
                text: "%1.",
                alignment: AlignmentType.START,
                style: {
                  paragraph: {
                    indent: { left: 720, hanging: 260 },
                  },
                },
              },
            ],
          },
        ],
      },
      sections: [
        {
          properties: {},
          children: elements,
        },
      ],
    });

    // Generate the document as a blob (browser-compatible)
    const blob = await Packer.toBlob(doc);

    // Use the filename with .docx extension
    const docxFilename = filename.endsWith(".docx")
      ? filename
      : `${filename}.docx`;

    saveAs(blob, docxFilename);
  } catch (error) {
    console.error("Error generating Word document:", error);
    throw new Error("Failed to generate Word document");
  }
}

// Helper function to create a Word table from markdown table rows
function createTableFromMarkdown(
  headerRow: string | null,
  dataRows: string[]
): Table | null {
  if (!headerRow && dataRows.length === 0) {
    return null;
  }

  // Parse header row if it exists
  const headerCells = headerRow
    ? headerRow
        .split("|")
        .map((cell) => cell.trim())
        .filter((cell) => cell.length > 0)
    : dataRows.length > 0
      ? dataRows[0]
          .split("|")
          .map((cell) => cell.trim())
          .filter((cell) => cell.length > 0)
      : [];

  if (headerCells.length === 0) {
    return null;
  }

  const tableRows: TableRow[] = [];

  // Create header row
  if (headerRow) {
    const headerCellElements = headerCells.map((cellText) => {
      const runs = parseInlineFormatting(cellText);
      return new TableCell({
        children: [
          new Paragraph({
            children:
              runs.length > 0 ? runs : [new TextRun({ text: cellText })],
          }),
        ],
        shading: {
          fill: "E5E7EB",
        },
      });
    });

    tableRows.push(
      new TableRow({
        children: headerCellElements,
      })
    );
  }

  // Create data rows
  const rowsToProcess = headerRow ? dataRows : dataRows.slice(1);
  for (const row of rowsToProcess) {
    const cells = row
      .split("|")
      .map((cell) => cell.trim())
      .filter((cell) => cell.length > 0);

    // Only process rows that have the same number of cells as the header
    if (cells.length === headerCells.length) {
      const cellElements = cells.map((cellText) => {
        const runs = parseInlineFormatting(cellText);
        return new TableCell({
          children: [
            new Paragraph({
              children:
                runs.length > 0 ? runs : [new TextRun({ text: cellText })],
            }),
          ],
        });
      });

      tableRows.push(
        new TableRow({
          children: cellElements,
        })
      );
    }
  }

  if (tableRows.length === 0) {
    return null;
  }

  // Calculate column widths (equal distribution)
  const columnWidth = Math.floor(10000 / headerCells.length); // 10000 is total width in twips

  return new Table({
    rows: tableRows,
    width: {
      size: 100,
      type: WidthType.PERCENTAGE,
    },
    columnWidths: Array.from({ length: headerCells.length }, () => columnWidth),
  });
}

const WORD_BLOCK_GAP_TWIPS = 240;

// Pages ignores paragraph spacing on unstyled paragraphs that come before the
// first styled one, so body paragraphs name the Normal style explicitly.
const WORD_BODY_STYLE = "Normal";

const MARKDOWN_HARD_BREAK_RE = /(?:\\| {2,})$/;

function hasMarkdownHardBreak(line: string): boolean {
  return MARKDOWN_HARD_BREAK_RE.test(line);
}

function normalizeMarkdownLineForWord(line: string): string {
  return line
    .trim()
    .replace(/\\$/, "")
    .replace(/\\(.)/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
}

function startsMarkdownBlock(line: string): boolean {
  if (
    /^(?:#{1,6}\s|>\s|[-*]\s|\d+\.\s|```|~~~|!\[)/.test(line) ||
    /^(?:---|\*\*\*|___)$/.test(line)
  ) {
    return true;
  }

  return (line.match(/\|/g) ?? []).length >= 2;
}

/** Parse markdown content and convert to Word document paragraphs and tables. */
export function parseMarkdownForWord(
  markdown: string,
  imageMap?: Map<string, WordImageData>
): (Paragraph | Table)[] {
  const lines = foldBlankHardBreaks(markdown).split("\n");
  const elements: (Paragraph | Table)[] = [];
  const listItemSpacing = (nextLine: string | undefined) =>
    nextLine !== undefined && /^\s*(?:[-*]|\d+\.)\s/.test(nextLine)
      ? undefined
      : { after: WORD_BLOCK_GAP_TWIPS };
  let inCodeBlock = false;
  let codeBlockContent: string[] = [];
  let inTable = false;
  let tableRows: string[] = [];
  let tableHeaderRow: string | null = null;

  const appendHardBreakContinuations = (
    runs: TextRun[],
    startIndex: number,
    continuationPrefix?: string
  ): number => {
    let currentLine = lines[startIndex];
    let nextLineIndex = startIndex;

    while (
      hasMarkdownHardBreak(currentLine) &&
      nextLineIndex + 1 < lines.length
    ) {
      const nextLine = lines[nextLineIndex + 1];
      let continuation = normalizeMarkdownLineForWord(nextLine);
      if (continuationPrefix && continuation.startsWith(continuationPrefix)) {
        continuation = continuation.slice(continuationPrefix.length);
      } else if (startsMarkdownBlock(continuation)) {
        break;
      }
      if (!continuation) break;

      runs.push(new TextRun({ text: "", break: 1 }));
      runs.push(...parseInlineFormatting(continuation));
      currentLine = nextLine;
      nextLineIndex += 1;
    }

    return nextLineIndex;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmedLine = normalizeMarkdownLineForWord(line);

    // Check if this is a table row (contains | characters and has at least 2 columns)
    // A proper markdown table row should have | at start/end or multiple | characters
    const pipeCount = (trimmedLine.match(/\|/g) || []).length;
    const isTableRow = pipeCount >= 2 && trimmedLine.includes("|");
    // Table separator row: contains dashes and pipes, like |---|---| or |:---|:---:|
    const isTableSeparator =
      /^\|?[\s]*:?-+:?[\s]*(\|[\s]*:?-+:?[\s]*)*\|?$/.test(trimmedLine);

    // Handle code blocks
    if (trimmedLine.startsWith("```")) {
      if (inCodeBlock) {
        // End code block
        if (codeBlockContent.length > 0) {
          elements.push(
            new Paragraph({
              style: WORD_BODY_STYLE,
              children: [
                new TextRun({
                  text: codeBlockContent.join("\n"),
                  font: "Courier New",
                  size: 20, // 10pt
                  color: "333333",
                }),
              ],
              spacing: { before: 200, after: 200 },
              indent: { left: 720 },
            })
          );
        }
        codeBlockContent = [];
        inCodeBlock = false;
      } else {
        // Start code block
        inCodeBlock = true;
      }
      continue;
    }

    if (inCodeBlock) {
      codeBlockContent.push(line);
      continue;
    }

    // Handle table rows
    if (isTableRow && !isTableSeparator) {
      if (!inTable) {
        inTable = true;
        tableRows = [];
        tableHeaderRow = null;
      }
      tableRows.push(trimmedLine);
      continue;
    } else if (isTableSeparator) {
      // This is the separator row, mark the previous row as header
      if (inTable && tableRows.length > 0) {
        tableHeaderRow = tableRows[tableRows.length - 1];
        tableRows = tableRows.slice(0, -1);
      }
      continue;
    } else if (inTable) {
      // End of table - process it
      if (tableRows.length > 0 || tableHeaderRow) {
        const table = createTableFromMarkdown(tableHeaderRow, tableRows);
        if (table) {
          elements.push(
            table,
            new Paragraph({ style: WORD_BODY_STYLE, children: [] })
          );
        }
      }
      inTable = false;
      tableRows = [];
      tableHeaderRow = null;
    }

    if (!trimmedLine) continue;

    const isEmptyEditorParagraph = !trimmedLine.replaceAll(
      ZERO_WIDTH_SPACE,
      ""
    );
    if (isEmptyEditorParagraph) {
      elements.push(new Paragraph({ style: WORD_BODY_STYLE, children: [] }));
      continue;
    }

    // Handle headers with better spacing.
    // Route through parseInlineFormatting so inline markdown like `**bold**`,
    // `*italic*`, links, etc. inside a heading don't leak through as literal
    // characters. Bold + size defaults apply uniformly to every emitted run.
    if (trimmedLine.startsWith("# ")) {
      elements.push(
        new Paragraph({
          heading: HeadingLevel.HEADING_1,
          children: parseInlineFormatting(trimmedLine.substring(2), {
            bold: true,
            size: 32, // 16pt
          }),
          spacing: { before: 360, after: WORD_BLOCK_GAP_TWIPS },
        })
      );
      continue;
    }

    if (trimmedLine.startsWith("## ")) {
      elements.push(
        new Paragraph({
          heading: HeadingLevel.HEADING_2,
          children: parseInlineFormatting(trimmedLine.substring(3), {
            bold: true,
            size: 28, // 14pt
          }),
          spacing: { before: 280, after: WORD_BLOCK_GAP_TWIPS },
        })
      );
      continue;
    }

    if (trimmedLine.startsWith("### ")) {
      elements.push(
        new Paragraph({
          heading: HeadingLevel.HEADING_3,
          children: parseInlineFormatting(trimmedLine.substring(4), {
            bold: true,
            size: 26, // 13pt
          }),
          spacing: { before: 200, after: WORD_BLOCK_GAP_TWIPS },
        })
      );
      continue;
    }

    if (trimmedLine.startsWith("#### ")) {
      elements.push(
        new Paragraph({
          heading: HeadingLevel.HEADING_4,
          children: parseInlineFormatting(trimmedLine.substring(5), {
            bold: true,
            size: 24, // 12pt
          }),
          spacing: { before: 160, after: WORD_BLOCK_GAP_TWIPS },
        })
      );
      continue;
    }

    // Handle blockquotes
    if (trimmedLine.startsWith("> ")) {
      const text = trimmedLine.substring(2);
      const runs = parseInlineFormatting(text);
      i = appendHardBreakContinuations(runs, i, "> ");
      elements.push(
        new Paragraph({
          style: WORD_BODY_STYLE,
          children: runs,
          indent: { left: 720 },
          spacing: { after: WORD_BLOCK_GAP_TWIPS },
        })
      );
      continue;
    }

    // Handle bullet points with indentation support
    const bulletMatch = trimmedLine.match(/^(\s*)[-*]\s(.+)/);
    if (bulletMatch) {
      const indentLevel = Math.floor(bulletMatch[1].length / 2);
      const text = bulletMatch[2];
      const runs = parseInlineFormatting(text);
      i = appendHardBreakContinuations(runs, i);
      elements.push(
        new Paragraph({
          bullet: {
            level: Math.min(indentLevel, 8), // Max 9 levels (0-8)
          },
          children: runs,
          spacing: listItemSpacing(lines[i + 1]),
        })
      );
      continue;
    }

    // Handle numbered lists with indentation support
    const numberedMatch = trimmedLine.match(/^(\s*)\d+\.\s(.+)/);
    if (numberedMatch) {
      const indentLevel = Math.floor(numberedMatch[1].length / 2);
      const text = numberedMatch[2];
      const runs = parseInlineFormatting(text);
      i = appendHardBreakContinuations(runs, i);
      elements.push(
        new Paragraph({
          numbering: {
            reference: "default-numbering",
            level: Math.min(indentLevel, 8), // Max 9 levels (0-8)
          },
          children: runs,
          spacing: listItemSpacing(lines[i + 1]),
        })
      );
      continue;
    }

    // Handle horizontal rules
    if (
      trimmedLine === "---" ||
      trimmedLine === "***" ||
      trimmedLine === "___"
    ) {
      elements.push(
        new Paragraph({
          style: WORD_BODY_STYLE,
          children: [new TextRun({ text: "" })],
          border: {
            bottom: {
              color: "000000",
              space: 1,
              style: "single",
              size: 6,
            },
          },
          spacing: { before: 200, after: 200 },
        })
      );
      continue;
    }

    // Handle markdown images: ![alt](url)
    const imageMatch = trimmedLine.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
    if (imageMatch) {
      const [, altText, imageUrl] = imageMatch;
      const imgData = imageMap?.get(imageUrl);
      if (imgData) {
        elements.push(
          new Paragraph({
            style: WORD_BODY_STYLE,
            children: [
              new ImageRun({
                type: "png",
                data: imgData.data,
                transformation: {
                  width: imgData.width,
                  height: imgData.height,
                },
                altText: {
                  title: altText || "Image",
                  description: altText || "Image",
                  name: altText || "Image",
                },
              }),
            ],
            spacing: { before: 200, after: 200 },
          })
        );
      } else {
        // Fallback: render alt text as a paragraph
        elements.push(
          new Paragraph({
            style: WORD_BODY_STYLE,
            children: [
              new TextRun({
                text: altText || "[Image]",
                italics: true,
                color: "666666",
              }),
            ],
            spacing: { after: WORD_BLOCK_GAP_TWIPS },
          })
        );
      }
      continue;
    }

    // Handle regular paragraphs
    const runs = parseInlineFormatting(trimmedLine);
    if (runs.length > 0) {
      i = appendHardBreakContinuations(runs, i);
      elements.push(
        new Paragraph({
          style: WORD_BODY_STYLE,
          children: runs,
          spacing: { after: WORD_BLOCK_GAP_TWIPS },
        })
      );
    }
  }

  // Handle table if we're still in one at the end
  if (inTable && (tableRows.length > 0 || tableHeaderRow)) {
    const table = createTableFromMarkdown(tableHeaderRow, tableRows);
    if (table) {
      elements.push(table);
    }
  }

  return elements;
}

// Parse inline formatting like **bold**, *italic*, etc.
// `defaults` is merged into every emitted TextRun's options so callers
// (e.g. heading branches) can apply uniform styling — bold, size — while
// per-run options like italics or font still take precedence on top.
function parseInlineFormatting(
  text: string,
  defaults: Partial<IRunOptions> = {}
): TextRun[] {
  const runs: TextRun[] = [];

  // First, handle escaped characters by removing the backslashes
  let cleanedText = text
    .replace(/\\(.)/g, "$1") // Remove ALL backslashes before any character
    .replace(/\s+/g, " ") // Normalize multiple spaces to single space
    .trim();

  // More comprehensive regex for markdown formatting including links
  const parts = cleanedText.split(
    /(\*\*.*?\*\*|\*(?!\*).*?\*|`.*?`|\[.*?\]\(.*?\)|~~.*?~~|__.*?__|_(?!_).*?_)/
  );

  for (const part of parts) {
    if (part.startsWith("**") && part.endsWith("**")) {
      // Bold text
      runs.push(
        new TextRun({
          ...defaults,
          text: part.slice(2, -2),
          bold: true,
        })
      );
    } else if (part.startsWith("__") && part.endsWith("__")) {
      // Bold text (alternative syntax)
      runs.push(
        new TextRun({
          ...defaults,
          text: part.slice(2, -2),
          bold: true,
        })
      );
    } else if (
      (part.startsWith("*") && part.endsWith("*") && !part.startsWith("**")) ||
      (part.startsWith("_") && part.endsWith("_") && !part.startsWith("__"))
    ) {
      // Italic text
      const textContent = part.startsWith("*")
        ? part.slice(1, -1)
        : part.slice(1, -1);
      runs.push(
        new TextRun({
          ...defaults,
          text: textContent,
          italics: true,
        })
      );
    } else if (part.startsWith("~~") && part.endsWith("~~")) {
      // Strikethrough text
      runs.push(
        new TextRun({
          ...defaults,
          text: part.slice(2, -2),
          strike: true,
        })
      );
    } else if (part.startsWith("`") && part.endsWith("`")) {
      // Inline code
      runs.push(
        new TextRun({
          ...defaults,
          text: part.slice(1, -1),
          font: "Courier New",
          size: 20, // 10pt
          color: "666666",
        })
      );
    } else if (part.match(/^\[.*?\]\(.*?\)$/)) {
      // Links [text](url)
      const linkMatch = part.match(/^\[(.*?)\]\((.*?)\)$/);
      if (linkMatch) {
        const [, linkText] = linkMatch;
        runs.push(
          new TextRun({
            ...defaults,
            text: linkText,
            color: "0066CC",
            underline: {},
          })
        );
        // Note: docx library doesn't have simple hyperlink support in this version
        // The text will be blue and underlined to indicate it was a link
      }
    } else if (part.trim() !== "") {
      // Regular text - handle potential line breaks and spaces
      runs.push(
        new TextRun({
          ...defaults,
          text: part,
        })
      );
    }
  }

  // If no runs were created, add the cleaned text
  if (runs.length === 0 && cleanedText.trim() !== "") {
    runs.push(
      new TextRun({
        ...defaults,
        text: cleanedText,
      })
    );
  }

  return runs;
}

// ── Legacy helpers ────────────────────────────────────────────────────────────

// this is not needed if assemblyai return structured json
export async function formatSummary(text: string): Promise<string> {
  marked.setOptions({
    gfm: true,
    breaks: true,
  });

  const sections = text.split(/\n(?=[A-Z])/);

  const formattedSections = sections.map((section) => {
    const [header, ...content] = section.split(":");
    if (content.length) {
      const contentText = content.join(":").trim();
      // Convert bullet points to proper Markdown list items
      const markdownContent = contentText.replace(/^-/gm, "*");
      return `## ${header.trim()}\n\n${markdownContent}`;
    } else {
      return `## ${section.trim()}`;
    }
  });

  const markdown = formattedSections.join("\n\n");
  return marked(markdown);
}
